import { create } from "zustand";
import {
  buyUpgrade,
  deserializeRun,
  draftCard,
  finishStage,
  leaveShop,
  pickPathways,
  serializeRun,
  startRun,
  trimCard,
  type RunState,
  type UpgradeKind,
} from "@reagent/meta";
import { ERA_1_POOL, type EraPoolDef } from "@reagent/content";
import type { CardInstance, RunTallies } from "@reagent/sim";

/**
 * The run: which stage you're on, what's in your deck, what you've bought.
 *
 * All the logic lives in @reagent/meta as pure functions, so this store is a
 * holder and a bridge to localStorage — nothing here decides anything. That
 * split is what lets a whole run be tested in node.
 */

const SAVE_KEY = "reagent.run.v1";

/**
 * localStorage throws rather than returning null in a few real situations —
 * Safari private browsing, storage disabled, quota exhausted — and a game that
 * won't start because it couldn't read a save is worse than one that forgets.
 */
function readSave(): RunState | null {
  try {
    return deserializeRun(window.localStorage.getItem(SAVE_KEY));
  } catch {
    return null;
  }
}

function writeSave(run: RunState): void {
  try {
    window.localStorage.setItem(SAVE_KEY, serializeRun(run));
  } catch {
    // Nothing to do and nothing worth interrupting the player for.
  }
}

function clearSave(): void {
  try {
    window.localStorage.removeItem(SAVE_KEY);
  } catch {
    /* ignore */
  }
}

/** A fresh seed per run, so a new run is a new shuffle. */
function newSeed(): number {
  return (Date.now() ^ Math.floor(Math.random() * 0xffffffff)) >>> 0;
}

interface RunStoreState {
  run: RunState;
  /** True when this run came off disk rather than being started fresh. */
  resumed: boolean;

  newRun: (seed?: number, pool?: EraPoolDef) => void;
  /** Commits the two-pathway pick (first = stage 1) and starts the run proper. */
  pickPathways: (first: string, second: string, pool?: EraPoolDef) => void;
  /**
   * Records the stage outcome and opens the shop, or ends the run.
   * `deckCards`, when given, is `GridSnapshot.deckCards` as the stage
   * ended — `finishStage` reconciles it into `run.deck` (spec 0007).
   */
  completeStage: (
    tallies: RunTallies,
    cleared: boolean,
    deckCards?: readonly CardInstance[],
    setupSpentCredits?: number,
  ) => void;
  continueToNextStage: () => void;

  draft: (cardId: string) => void;
  trim: (cardId: string) => void;
  upgrade: (kind: UpgradeKind) => void;
}

const saved = readSave();

export const useRunStore = create<RunStoreState>((set, get) => {
  /** Every mutation goes through here, so a run is never saved half-applied. */
  const commit = (next: RunState) => {
    writeSave(next);
    set({ run: next });
  };

  return {
    run: saved ?? startRun(newSeed()),
    resumed: saved !== null,

    newRun: (seed, pool = ERA_1_POOL) => {
      const run = startRun(seed ?? newSeed(), pool);
      clearSave();
      writeSave(run);
      set({ run, resumed: false });
    },

    pickPathways: (first, second, pool = ERA_1_POOL) =>
      commit(pickPathways(get().run, first, second, pool)),

    completeStage: (tallies, cleared, deckCards, setupSpentCredits) =>
      commit(finishStage(get().run, tallies, cleared, deckCards, setupSpentCredits)),
    continueToNextStage: () => commit(leaveShop(get().run)),

    draft: (cardId) => commit(draftCard(get().run, cardId)),
    trim: (cardId) => commit(trimCard(get().run, cardId)),
    upgrade: (kind) => commit(buyUpgrade(get().run, kind)),
  };
});
